// ABOUTME: Button that copies the collection-level share URL to the clipboard.
// ABOUTME: Uses CopyToClipboardStore pattern with the collection's share URL resource.
import { isJust } from '@execonline-inc/structured-maybe';
import { nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import { useMemo } from 'react';
import CopyToClipboardStore from '../../../../../CopyToClipboard/Store';
import CopyToClipboardReactions from '../../../../../CopyToClipboard/Store/Reactions';
import Checkmark from '../../../../../Cutesies/Checkmark';
import Loader from '../../../../../Loader';
import { emptyFragment } from '../../../../../../EmptyFragment';
import { findLink } from '../../../../../../LinkyLinky';
import { T, TranslationsContext, translation } from '../../../../../../Translations';
import { ShareableLinksResource } from '../Types';
import ShareableLinkIcon from '../../../../../Cutesies/ShareableLinkIcon';

interface Props {
  learningCollectionResource: ShareableLinksResource;
}

function CopyCollectionLinkButton({ learningCollectionResource }: Props) {
  const { payload } = learningCollectionResource;
  const shareUrlMaybe = payload.shareUrl;

  if (!isJust(shareUrlMaybe)) {
    return emptyFragment();
  }

  const shareUrlResource = shareUrlMaybe.value;
  return findLink('shared-url-short-link', shareUrlResource.links)
    .map((link) => <CopyCollectionLinkInner copyText={link.href} />)
    .getOrElse(emptyFragment);
}

interface InnerProps {
  copyText: string;
}

interface StateProps {
  kind: 'waiting' | 'copying' | 'copied';
  copying: () => void;
}

const CopyCollectionLinkState = observer(function CopyCollectionLinkState({
  kind,
}: Omit<StateProps, 'copying'>) {
  switch (kind) {
    case 'waiting':
      return (
        <span className="flex items-center gap-1">
          <ShareableLinkIcon className="h-4 w-4" />
          <T kind={'Copy Collection Link'} />
        </span>
      );
    case 'copying':
      return (
        <div className="">
          <Loader type="spin" text={nothing()} height={24} width={24} color="var(--primary)" />
        </div>
      );
    case 'copied':
      return (
        <div className="flex items-center gap-1">
          <Checkmark className="fill-primary" />
          <T kind={'Copy Collection Link'} />
        </div>
      );
  }
});

const CopyCollectionLinkInner = observer(function CopyCollectionLinkInner({
  copyText,
}: InnerProps) {
  const store = useMemo(() => new CopyToClipboardStore(copyText), [copyText]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      store.copying();
    }
  };

  return (
    <TranslationsContext.Consumer>
      {(ts) => (
        <div
          role="button"
          tabIndex={0}
          aria-label={translation('Copy Collection Link')(ts)}
          data-clipboard-text={copyText}
          className="bg-card border-1 inline-flex items-center rounded-md border-primary text-primary py-2 px-4 text-sm cursor-pointer focus:ring-2 focus:ring-ring focus:ring-offset-2"
          data-intercom-target="shareable-links-copy-collection-link-button"
          onClick={() => store.copying()}
          onKeyDown={handleKeyDown}
        >
          <CopyCollectionLinkState kind={store.state.kind} />
          <CopyToClipboardReactions store={store} />
        </div>
      )}
    </TranslationsContext.Consumer>
  );
});

export default observer(CopyCollectionLinkButton);
